import { useMemo, useCallback, useLayoutEffect, useReducer } from 'react';
import { MenuItemActionType } from './actions';
import reducer, { initialMenuItemState } from './reducer';
export const useMenu = ({
  id,
  menuTriggerId,
  ariaLabel,
  role,
  closeMenu,
  setFocusOnMount,
  shouldManageTabIndex = false,
  menuOrientation = 'vertical',
  shouldLoopFocus = false
}) => {
  const [menuItemsState, dispatch] = useReducer(reducer, Object.assign({}, initialMenuItemState, {
    shouldManageTabIndex
  }));
  const setFocusToFirstMenuItem = useCallback(() => {
    dispatch({
      type: MenuItemActionType.FOCUS_FIRST_MENU_ITEM
    });
  }, [dispatch]);
  const setFocusToLastMenuItem = useCallback(() => {
    dispatch({
      type: MenuItemActionType.FOCUS_LAST_MENU_ITEM
    });
  }, [dispatch]);
  const setFocusToNextItem = useCallback(() => {
    dispatch({
      type: MenuItemActionType.FOCUS_NEXT_MENU_ITEM,
      shouldLoopFocus
    });
  }, [dispatch, shouldLoopFocus]);
  const setFocusToPreviousItem = useCallback(() => {
    dispatch({
      type: MenuItemActionType.FOCUS_PREVIOUS_MENU_ITEM,
      shouldLoopFocus
    });
  }, [dispatch, shouldLoopFocus]);
  const resetFocus = useCallback(() => {
    dispatch({
      type: MenuItemActionType.FOCUS_RESET
    });
  }, [dispatch]);
  const setMenuItem = useCallback(({
    id: menuItemId,
    visuallyHidden
  }) => {
    dispatch({
      type: MenuItemActionType.ADD_MENU_ITEM,
      id: menuItemId,
      visuallyHidden
    });
  }, []);
  const hideMenuItem = useCallback(({
    id: menuItemId
  }) => {
    dispatch({
      type: MenuItemActionType.HIDE_MENU_ITEM,
      id: menuItemId
    });
  }, []);
  const setActiveSubmenu = useCallback(({
    id: submenuId
  }) => {
    dispatch({
      type: MenuItemActionType.SET_ACTIVE_SUBMENU,
      id: submenuId
    });
  }, []);
  const setFocusToMenuTrigger = useCallback(() => {
    const menuTriggerEle = menuTriggerId ? document.getElementById(menuTriggerId) : undefined;
    if (menuTriggerEle) {
      menuTriggerEle.focus();
    }
  }, [menuTriggerId]);
  const exitMenu = useCallback(() => {
    if (closeMenu) {
      setFocusToMenuTrigger();
      closeMenu();
    }
  }, [closeMenu, setFocusToMenuTrigger]);
  const onMenuItemKeyDown = useCallback(e => {
    let stopDefaults = false;
    switch (e.key) {
      case 'Esc':
      case 'Escape':
      case 'Tab':
        resetFocus();
        if (closeMenu) {
          exitMenu();
          stopDefaults = true;
        }
        break;
      case 'Up':
      case 'ArrowUp':
        if (menuOrientation === 'vertical') {
          setFocusToPreviousItem();
          stopDefaults = true;
        }
        break;
      case 'Left':
      case 'ArrowLeft':
        if (menuOrientation === 'horizontal') {
          setFocusToPreviousItem();
          stopDefaults = true;
        }
        break;
      case 'Down':
      case 'ArrowDown':
        if (menuOrientation === 'vertical') {
          setFocusToNextItem();
          stopDefaults = true;
        }
        break;
      case 'Right':
      case 'ArrowRight':
        if (menuOrientation === 'horizontal') {
          setFocusToNextItem();
          stopDefaults = true;
        }
        break;
      case 'Home':
      case 'PageUp':
        setFocusToFirstMenuItem();
        stopDefaults = true;
        break;
      case 'End':
      case 'PageDown':
        setFocusToLastMenuItem();
        stopDefaults = true;
        break;
      default:
        return;
    }
    if (stopDefaults) {
      e.stopPropagation();
      e.preventDefault();
    }
  }, [closeMenu, exitMenu, menuOrientation, resetFocus, setFocusToFirstMenuItem, setFocusToLastMenuItem, setFocusToNextItem, setFocusToPreviousItem]);
  const hasFocusableItems = menuItemsState.focusOrder.length > 0;
  useLayoutEffect(() => {
    if (hasFocusableItems && setFocusOnMount) {
      setFocusToFirstMenuItem();
    }
  }, [hasFocusableItems, setFocusOnMount, setFocusToFirstMenuItem]);
  const menuProps = useMemo(() => Object.assign({
    id,
    role: role || 'menu',
    'aria-orientation': menuOrientation
  }, ariaLabel && {
    'aria-label': ariaLabel
  }, menuTriggerId && {
    'aria-labelledby': menuTriggerId
  }), [id, role, menuOrientation, ariaLabel, menuTriggerId]);
  const providerProps = useMemo(() => ({
    menuItemsState,
    setMenuItem,
    hideMenuItem,
    onMenuItemKeyDown,
    setActiveSubmenu
  }), [menuItemsState, setMenuItem, hideMenuItem, onMenuItemKeyDown, setActiveSubmenu]);
  return {
    providerProps,
    menuProps
  };
};